if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        this.mediaVariantMap = this.buildMediaVariantMap();
        
        // Listen for slide changes (mobile)
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        if (!this.elements.thumbnails) return;

        // Thumbnail clicks (desktop) - add variant sync here
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', () => {
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
              // Also sync variant on desktop thumbnail click
              this.syncVariantFromMedia(mediaToSwitch.dataset.target);
            });
        });
        
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      buildMediaVariantMap() {
        const map = {};
        const productData = document.querySelector('[data-product-json]');
        if (!productData) return map;
        
        try {
          const product = JSON.parse(productData.textContent);
          product.variants.forEach((variant) => {
            if (variant.featured_media) {
              map[variant.featured_media.id] = variant.id;
            }
          });
        } catch (e) {
          console.warn('Could not parse product JSON', e);
        }
        return map;
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }
        
        this.syncVariantFromMedia(mediaId);
      }

      syncVariantFromMedia(mediaId) {
        const parts = mediaId.split('-');
        const actualMediaId = parts[parts.length - 1];
        const variantId = this.mediaVariantMap[actualMediaId];
        
        if (!variantId) return;
        
        const productData = document.querySelector('[data-product-json]');
        if (!productData) return;
        
        try {
          const product = JSON.parse(productData.textContent);
          const variant = product.variants.find(v => v.id === variantId);
          if (!variant) return;
          
          // Update URL
          const url = new URL(window.location);
          url.searchParams.set('variant', variantId);
          window.history.replaceState({}, '', url);
          
          // Update swatches
          const variantSelects = document.querySelector('variant-selects');
          if (variantSelects) {
            variantSelects.querySelectorAll('input.swatch-input__input').forEach(input => {
              input.checked = false;
              input.removeAttribute('checked');
            });
            
            variant.options.forEach((optionValue, index) => {
              const input = variantSelects.querySelector(`input.swatch-input__input[value="${CSS.escape(optionValue)}"]`);
              if (input) {
                input.checked = true;
                input.setAttribute('checked', 'checked');
                
                const fieldset = input.closest('fieldset');
                if (fieldset) {
                  const legend = fieldset.querySelector('legend.form__label');
                  if (legend) {
                    const optionName = product.options[index];
                    legend.innerHTML = `${optionName}: <span>${optionValue}</span>`;
                  }
                }
              }
            });
          }
          
          // Update cart form
          const variantInput = document.querySelector('input[name="id"][form]') || 
                               document.querySelector('form[action*="/cart/add"] input[name="id"]');
          if (variantInput) {
            variantInput.value = variantId;
            variantInput.setAttribute('value', variantId);
          }
          
          // Dispatch event for preview script
          document.dispatchEvent(new CustomEvent('variantChangedFromSlide', {
            detail: { variantId: variantId, variant: variant, mediaId: mediaId }
          }));
          
        } catch (e) {
          console.warn('Error syncing variant:', e);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;
        
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}